/* eslint-disable react-refresh/only-export-components */
import { DownOutlined } from "./icons";
import { Button, LiveRegion } from "./controls";
import "./CursorPagination.css";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

export interface CursorPageResult<T> {
  items: T[];
  nextCursor: string;
  hasMore: boolean;
}

export interface UseCursorPaginationOptions<T> {
  enabled?: boolean;
  loadPage: (
    cursor: string,
    signal: AbortSignal,
  ) => Promise<CursorPageResult<T>>;
  onPage?: (page: CursorPageResult<T>) => void;
  resetKey: string;
}

export interface CursorPaginationState<T> {
  error: unknown;
  hasMore: boolean;
  isInitialLoading: boolean;
  isLoadingMore: boolean;
  items: T[];
  loadMore: () => Promise<void>;
  reload: () => void;
  setItems: Dispatch<SetStateAction<T[]>>;
}

export function useCursorPagination<T>({
  enabled = true,
  loadPage,
  onPage,
  resetKey,
}: UseCursorPaginationOptions<T>): CursorPaginationState<T> {
  const [items, setItems] = useState<T[]>([]);
  const [nextCursor, setNextCursor] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [isInitialLoading, setInitialLoading] = useState(enabled);
  const [isLoadingMore, setLoadingMore] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);
  const generationRef = useRef(0);
  const inFlightRef = useRef(false);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const generation = ++generationRef.current;
    controllerRef.current?.abort();
    inFlightRef.current = false;
    queueMicrotask(() => {
      if (generation !== generationRef.current) return;
      setItems([]);
      setNextCursor("");
      setHasMore(false);
      setError(null);
      setLoadingMore(false);
      setInitialLoading(enabled);
    });
    if (!enabled) {
      return;
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    inFlightRef.current = true;
    void loadPage("", controller.signal)
      .then((page) => {
        if (controller.signal.aborted || generation !== generationRef.current) {
          return;
        }
        setItems(page.items);
        setNextCursor(page.nextCursor);
        setHasMore(page.hasMore && page.nextCursor !== "");
        onPage?.(page);
      })
      .catch((reason: unknown) => {
        if (
          !controller.signal.aborted &&
          generation === generationRef.current
        ) {
          setError(reason);
        }
      })
      .finally(() => {
        if (generation === generationRef.current) {
          inFlightRef.current = false;
          setInitialLoading(false);
        }
      });
    return () => {
      controller.abort();
      controllerRef.current?.abort();
    };
  }, [enabled, loadPage, onPage, reloadVersion, resetKey]);

  const loadMore = useCallback(async () => {
    if (!enabled || !hasMore || !nextCursor || inFlightRef.current) return;
    const generation = generationRef.current;
    const controller = new AbortController();
    controllerRef.current = controller;
    inFlightRef.current = true;
    setLoadingMore(true);
    setError(null);
    try {
      const page = await loadPage(nextCursor, controller.signal);
      if (controller.signal.aborted || generation !== generationRef.current) {
        return;
      }
      setItems((current) => [...current, ...page.items]);
      setNextCursor(page.nextCursor);
      onPage?.(page);
      setHasMore(page.hasMore && page.nextCursor !== "");
    } catch (reason) {
      if (!controller.signal.aborted && generation === generationRef.current) {
        setError(reason);
      }
    } finally {
      if (generation === generationRef.current) {
        inFlightRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [enabled, hasMore, loadPage, nextCursor, onPage]);

  const reload = useCallback(() => {
    setReloadVersion((current) => current + 1);
  }, []);

  return {
    error,
    hasMore,
    isInitialLoading,
    isLoadingMore,
    items,
    loadMore,
    reload,
    setItems,
  };
}

export function CursorPagination({
  hasMore,
  isLoading,
  itemCount,
  itemLabel = "项",
  loadMoreLabel = "加载更多",
  onLoadMore,
}: {
  hasMore: boolean;
  isLoading: boolean;
  itemCount: number;
  itemLabel?: string;
  loadMoreLabel?: string;
  onLoadMore: () => void;
}) {
  if (!hasMore && !isLoading) return null;
  return (
    <LiveRegion
      as="div"
      busy={isLoading}
      className="ark-cursor-pagination"
    >
      <Button
        className="ark-cursor-pagination-action"
        disabled={!hasMore}
        icon={<DownOutlined />}
        loading={isLoading}
        onClick={onLoadMore}
      >
        {loadMoreLabel}
      </Button>
      <span className="ark-cursor-pagination-summary">
        已加载 {itemCount} {itemLabel}
      </span>
    </LiveRegion>
  );
}
